/**
 * Rainbow Mulch — Playground Project Builder
 * UI state machine (Sprint 3)
 *
 * ARCHITECTURE
 * Vanilla JS, no framework, no build step — see docs/10-theme-development-workflow.md §7.
 * State lives in ONE object and the DOM is rendered FROM it. State is never read
 * back out of the DOM. Not a framework, but the same discipline.
 *
 * WHAT IS REAL HERE
 *   - Step navigation, gating, and free backward editing (OQ-25 assumption)
 *   - Single 3" depth default pre-filled on load (D-020 as amended, BR-06b)
 *   - Tote acknowledgement gating (D-022)
 *   - Fixed-count colour selection (BR-16/BR-17)
 *   - Overlay focus management (D-019)
 *   - Freight quote state machine, including the D-004 failure path
 *
 * WHAT IS MOCKED (as of Sprint 4)
 *   Only freight and tax. All geometry, quantity and pricing is real, via
 *   PBCalculator.derive(). See assets/pb-mocks.js for what is left.
 *
 * DOM CONTRACT
 *   data-pb-panel="N"        step container
 *   data-pb-goto="N"         jump to step N
 *   data-pb-next / -back     step navigation
 *   data-pb-field="k"        text/number input bound to state[k]
 *   data-pb-out="k"          element displaying derived value k
 *   data-pb-panelout="k"     same, inside the project panel
 *   data-pb-sum="k"          same, inside the Step 5 summary
 */

(function () {
  'use strict';

  var cfg = window.PBConfig;
  var root = document.querySelector('[data-pb-root]');
  if (!root || !cfg) return;

  var TOTAL_STEPS = 5;

  /* =====================================================================
   * STATE — the single source of truth
   * =================================================================== */
  var state = {
    step: 1,
    maxStepReached: 1,

    // Step 1 — D-018, D-028
    shape: 'rectangle', // 'rectangle' | 'circle' — D-028, rectangle is the default
    length: null,
    width: null,
    diameter: null, // circle only — D-028
    depth: null, // pre-filled to PBConfig.dimensions.depthDefault on init

    // Step 3 — BR-07, D-022
    packaging: null, // 'bags' | 'tote'
    toteAcknowledged: false,

    // Step 2 — BR-15..BR-17, D-014
    colorMode: null, // 'single' | 'mix'  (D-027)
    blendCount: null, // 2 | 3 | 4
    colors: [],

    // Step 4 — SH-03
    zip: null,
    // Item 12 (2026-09-11) — consecutive 'invalid_zip' results in a row.
    // Reset to 0 on any OTHER freight outcome (quoted/estimated/oversized).
    // 1 -> prompt a ZIP re-entry. 2+ -> stop retrying against R+L and route
    // to a specialist/save instead — see fetchFreight()/renderFreightStates().
    zipErrorCount: 0,
    deliveryType: null, // 'residential' | 'commercial'
    // Item 11 (2026-09-11) — liftgate is no longer a customer choice. Always
    // 'yes' for a freight-truck order, from the moment the app loads; there
    // is no UI path that ever sets it to anything else. Kept as this exact
    // string (not a boolean) only because stepComplete(4) and the freight
    // request payload already expect 'yes'/'no' — changing the type would
    // touch more than the one thing item 11 asked for.
    liftgate: 'yes',
    freight: { status: 'none' } // 'none' | 'loading' | 'quoted' | 'estimated' | 'flatrate'
  };

  /* =====================================================================
   * HELPERS
   * =================================================================== */
  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }
  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }
  function toggle(el, on) { if (el) el.hidden = !on; }

  var money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  function fmtMoney(n) { return n === null || n === undefined ? '—' : money.format(n); }
  function fmtNum(n) { return n === null || n === undefined ? '—' : n.toLocaleString('en-US'); }

  /**
   * Square footage, rounded for display.
   *
   * Circle areas are irrational (π), so the raw value is e.g. 452.3893421...
   * and toLocaleString() renders "452.389 sq ft" — spurious precision that reads
   * as a measurement we do not have. The calculator keeps full precision for the
   * volume step; only the display is rounded, to one decimal place.
   *
   * Rectangles are unaffected: 432 stays "432".
   */
  function fmtArea(n) {
    if (n === null || n === undefined) return '—';
    return (Math.round(n * 10) / 10).toLocaleString('en-US');
  }

  /**
   * Screen-reader announcements. Two channels, deliberately:
   *   polite    — calculated values updating; must never interrupt
   *   assertive — freight failure; changes what the customer is agreeing to
   */
  function announce(msg, assertive) {
    var region = assertive ? $('[data-pb-live-assertive]') : $('[data-pb-live-polite]');
    if (!region) return;
    region.textContent = '';
    // Re-set on the next frame so repeated identical messages are still announced.
    window.requestAnimationFrame(function () { region.textContent = msg; });
  }

  function setText(attr, key, value) {
    $$('[data-pb-' + attr + '="' + key + '"]').forEach(function (el) {
      el.textContent = value;
    });
  }

  function parsePositive(raw) {
    if (raw === '' || raw === null || raw === undefined) return null;
    var n = Number(raw);
    if (!isFinite(n)) return null;
    return n;
  }

  /* =====================================================================
   * VALIDATION — OQ-09 working assumption only:
   * reject zero, negative and non-numeric. No min/max order is enforced.
   *
   * NOTE the asymmetry with depth guidance, and keep it: a zero length is
   * IMPOSSIBLE so it blocks. A shallow depth is a CHOICE we may disagree with,
   * so it only informs (D-015). Depth must never appear in validation logic.
   * =================================================================== */
  function fieldError(key) {
    var v = state[key];
    if (v === null) return null;
    if (!isFinite(v)) return 'Enter a number';
    if (v <= 0) return capitalise(key) + ' must be greater than zero';
    return null;
  }

  function capitalise(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  function renderFieldErrors() {
    ['length', 'width', 'diameter', 'depth'].forEach(function (key) {
      var el = $('[data-pb-error="' + key + '"]');
      if (!el) return;
      // Depth is validated for being a NUMBER only — never for being "too shallow".
      var msg = fieldError(key);
      if (msg) { el.textContent = msg; show(el); } else { hide(el); }
    });
  }

  function stepComplete(n) {
    switch (n) {
      case 1:
        // D-028 — which dimensions are required depends on the chosen shape.
        // Depth is pre-filled, so in practice the customer supplies either
        // length + width, or a single diameter.
        if (!(state.depth > 0)) return false;
        return state.shape === 'circle'
          ? state.diameter > 0
          : state.length > 0 && state.width > 0;
      case 2:
        if (state.colorMode === 'single') return state.colors.length === 1;
        if (state.colorMode === 'mix') {
          return !!state.blendCount && state.colors.length === state.blendCount;
        }
        return false;
      case 3:
        if (!state.packaging) return false;
        // D-022 — gate on the ACKNOWLEDGEMENT, never on the packaging choice.
        if (state.packaging === 'tote' && cfg.requireToteAcknowledgement) {
          return state.toteAcknowledged;
        }
        return true;
      case 4:
        // A labelled ESTIMATE, or an oversized-order referral (D-070), is
        // enough to proceed — D-004 / SH-05, SH-08 never block the customer.
        // 'invalid_zip' (item 12) is DELIBERATELY excluded: that ZIP genuinely
        // cannot ship, so there is nothing honest to let the customer proceed
        // with — the escape hatch there is the specialist/save routing shown
        // in that state, not Continue.
        return !!state.zip && !!state.deliveryType && !!state.liftgate &&
          (state.freight.status === 'quoted' || state.freight.status === 'estimated' || state.freight.status === 'oversized');
      case 5:
        return true;
      default:
        return false;
    }
  }

  /* =====================================================================
   * NAVIGATION
   *
   * OQ-25 STILL OPEN. Working assumption: customers may move freely backward
   * and edit any earlier step. To constrain this later, change
   * canNavigateBackward() — it is the single decision point.
   * =================================================================== */
  function canNavigateBackward(target) {
    if (!cfg.allowFreeBackwardEditing) return target === state.step - 1;
    return target <= state.maxStepReached;
  }

  function goToStep(n, opts) {
    if (n < 1 || n > TOTAL_STEPS) return;
    if (n > state.step && !stepComplete(state.step)) return;
    if (n < state.step && !canNavigateBackward(n)) return;

    state.step = n;
    state.maxStepReached = Math.max(state.maxStepReached, n);
    root.setAttribute('data-pb-step', String(n));

    $$('[data-pb-panel]').forEach(function (panel) {
      toggle(panel, Number(panel.getAttribute('data-pb-panel')) === n);
    });

    renderProgress();
    render();

    // Item 13 (2026-09-11) — the summary confetti swatch (if a blend is
    // selected) needs a repaint now that its container has real layout; see
    // the note on renderConfettiPreview for why an earlier paint attempt,
    // made while this panel was hidden, rendered nothing.
    if (n === 5) repaintSummaryConfetti();

    // Move focus to the new step's heading so keyboard and screen-reader users
    // land in the right place rather than at the top of the document.
    var heading = $('[data-pb-panel="' + n + '"] .pb__step-heading');
    if (heading) heading.focus();
    announce('Step ' + n + ' of ' + TOTAL_STEPS);
  }

  function renderProgress() {
    $$('[data-pb-goto]').forEach(function (btn) {
      var n = Number(btn.getAttribute('data-pb-goto'));
      // Summary "Edit" links share data-pb-goto but live outside the indicator.
      if (!btn.classList.contains('pb__progress-btn')) return;

      var reachable = n <= state.maxStepReached;
      btn.disabled = !reachable;
      btn.setAttribute('aria-current', n === state.step ? 'step' : 'false');
      btn.classList.toggle('is-current', n === state.step);
      btn.classList.toggle('is-done', n < state.step);

      var st = $('[data-pb-progress-state]', btn);
      if (st) {
        st.textContent = n === state.step ? 'current step'
          : reachable ? 'completed, editable'
          : 'not yet available';
      }
    });
  }

  /* =====================================================================
   * STEP 1 — Dimensions and the single 3" depth default (D-020 as amended, BR-06b)
   * =================================================================== */
  function wireStep1() {
    // D-028 — shape selector
    $$('[data-pb-shape]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        applyShape(input.value);
      });
    });
    applyShape(state.shape); // set initial field visibility from the default

    ['length', 'width', 'diameter', 'depth'].forEach(function (key) {
      var input = $('[data-pb-field="' + key + '"]');
      if (!input) return;
      input.addEventListener('input', function () {
        state[key] = parsePositive(input.value);

        // SH-13 / OQ-25 — dimensions changed, so any freight quote is now stale.
        invalidateFreight('dimensions');

        renderFieldErrors();
        render();
      });
    });
  }

  /**
   * D-028 — swap which dimension fields are visible for the chosen shape.
   *
   * Values from the hidden shape are deliberately KEPT in state rather than
   * cleared: a customer who tries Circle and switches back to Rectangle should
   * find their length and width still there. Only the ACTIVE shape's fields are
   * read by stepComplete(), so a stale value can never leak into a calculation.
   */
  function applyShape(shape) {
    state.shape = shape;

    $$('[data-pb-shape-fields]').forEach(function (el) {
      toggle(el, el.getAttribute('data-pb-shape-fields') === shape);
    });

    // Shape changes the area, so any fetched freight quote is stale (SH-13).
    invalidateFreight('shape');
    renderFieldErrors();
    render();

    announce(shape === 'circle'
      ? 'Circle selected. Enter the diameter in feet.'
      : 'Rectangle selected. Enter length and width in feet.');
  }

  /** Human-readable area line, shape-aware (D-028). */
  function areaLabel(d) {
    if (state.shape === 'circle') {
      return state.diameter > 0
        ? state.diameter + ' ft across · ' + fmtArea(d.squareFeet) + ' sq ft'
        : '—';
    }
    return state.length > 0 && state.width > 0
      ? state.length + ' × ' + state.width + ' ft · ' + fmtArea(d.squareFeet) + ' sq ft'
      : '—';
  }

  /**
   * Pre-fill the single recommended depth (D-020 corrected, BR-06b).
   * Applied once on load: there is no mode-dependent default to wait for.
   * D-015 — this is a DEFAULT, not a limit. The customer can change it freely.
   */
  function applyDepthDefault() {
    state.depth = cfg.dimensions.depthDefault;
    var depthInput = $('[data-pb-field="depth"]');
    if (depthInput) depthInput.value = String(state.depth);
  }

  /**
   * Item 1 (2026-09-11) — orders at or below PBConfig.freight.flatFee.
   * maxCubicFeet physically don't belong in a 40 cu ft tote and ship UPS at a
   * flat rate, not freight. Bulk Tote is hidden entirely (not merely
   * disabled — BR-08 is about a REAL choice, and a tote nobody could use for
   * an order this size is not one) and packaging is forced to Bags.
   *
   * Called from render() on every pass (driven by Step 1's billed cubic
   * feet), so it stays correct as dimensions change in either direction —
   * an order can cross the 14 cu ft line either way while the customer is
   * still on Step 1.
   */
  function applyFlatFeeConstraint(billedCubicFeet) {
    var eligible = window.PBCalculator.isFlatFeeEligible(billedCubicFeet);
    toggle($('[data-pb-tote-card]'), !eligible);

    if (!eligible || state.packaging !== 'tote') return;

    // Order shrank to or below the flat-fee line while Tote was selected —
    // force Bags, mirroring exactly what wireStep3's own change handler does
    // when a customer picks Bags themselves (including the tote-ack reset).
    state.packaging = 'bags';
    var bagsInput = $('input[data-pb-packaging][value="bags"]');
    if (bagsInput) bagsInput.checked = true;
    hide($('[data-pb-tote-ack]'));
    state.toteAcknowledged = false;
    var ackInput = $('[data-pb-tote-ack-input]');
    if (ackInput) ackInput.checked = false;
    announce('This order ships UPS at a flat rate, so it is Bag packaging only.');
  }

  /* =====================================================================
   * STEP 3 — Packaging + tote acknowledgement (BR-08, D-022)
   * Reordered after Colour/Color (item 2, 2026-09-11): Packaging no longer
   * needs to precede Colour — Colour has zero numeric consequence (D-016)
   * and Packaging only ever depended on Step 1's geometry, never on Colour.
   * =================================================================== */
  function wireStep3() {
    $$('[data-pb-packaging]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        state.packaging = input.value;

        // D-022 — reveal the acknowledgement for TOTES ONLY. The tote option
        // itself is never disabled or discouraged (BR-08 untouched).
        var ack = $('[data-pb-tote-ack]');
        if (state.packaging === 'tote') {
          show(ack);
        } else {
          hide(ack);
          state.toteAcknowledged = false;
          var ackInput = $('[data-pb-tote-ack-input]');
          if (ackInput) ackInput.checked = false;
        }

        // Packaging drives weight, so any existing freight quote is stale.
        invalidateFreight('packaging');
        render();
      });
    });

    var ackInput = $('[data-pb-tote-ack-input]');
    if (ackInput) {
      ackInput.addEventListener('change', function () {
        state.toteAcknowledged = ackInput.checked;
        render();
      });
    }
  }

  /* =====================================================================
   * STEP 2 — Color (BR-15..BR-17, D-014, D-016)
   * Color affects NOTHING numeric, so nothing here touches derived values.
   * Moved ahead of Packaging (item 2, 2026-09-11) — see the note on wireStep3.
   * =================================================================== */
  function wireStep2() {
    renderSwatches();

    // D-034 — illustrative Mix Your Own image, from config so the URL lives in
    // exactly one place.
    var mixImg = $('[data-pb-mix-image]');
    if (mixImg && cfg.color.mixYourOwnImage) mixImg.src = cfg.color.mixYourOwnImage;

    $$('[data-pb-colormode]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        state.colorMode = input.value;
        state.blendCount = state.colorMode === 'single' ? 1 : null;
        state.colors = [];
        $$('[data-pb-color]').forEach(function (c) { c.checked = false; });
        $$('[data-pb-blendcount]').forEach(function (c) { c.checked = false; });

        toggle($('[data-pb-blend-wrap]'), state.colorMode === 'mix');
        toggle($('[data-pb-mix-example]'), state.colorMode === 'mix');
        toggle($('[data-pb-colors-wrap]'), state.colorMode === 'single');
        hide($('[data-pb-blend-preview]'));
        renderColorGuidance();
        render();
      });
    });

    $$('[data-pb-blendcount]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        state.blendCount = Number(input.value);
        state.colors = [];
        $$('[data-pb-color]').forEach(function (c) { c.checked = false; });
        show($('[data-pb-colors-wrap]'));
        hide($('[data-pb-blend-preview]'));
        renderColorGuidance();
        render();
      });
    });
  }

  /** OQ-04 — catalogue is largely PLACEHOLDER. Rendered from config so the real list is a one-file change. */
  function renderSwatches() {
    var wrap = $('[data-pb-swatches]');
    if (!wrap) return;

    cfg.color.catalog.forEach(function (c) {
      var label = document.createElement('label');
      label.className = 'pb__swatch';

      var input = document.createElement('input');
      input.type = 'checkbox';
      input.value = c.id;
      input.setAttribute('data-pb-color', '');
      input.className = 'pb__swatch-input';

      // D-034 — real swatch photography. An <img> rather than a background so it
      // participates in lazy loading and has a real intrinsic size.
      // aria-hidden because the visible colour NAME below is the accessible
      // label; announcing both would just repeat it (never colour alone).
      var media = document.createElement('img');
      media.className = 'pb__swatch-media';
      media.src = c.swatch;
      media.alt = '';
      media.loading = 'lazy';
      media.decoding = 'async';
      media.setAttribute('aria-hidden', 'true');

      // Never colour alone — the visible name carries the meaning.
      var name = document.createElement('span');
      name.className = 'pb__swatch-name';
      name.textContent = c.name;

      label.appendChild(input);
      label.appendChild(media);
      label.appendChild(name);
      wrap.appendChild(label);

      input.addEventListener('change', function () { onColorToggle(input, c); });
    });
  }

  /**
   * Fixed-count multi-select. A pattern people get wrong constantly, so the
   * counter and the explicit hint exist to prevent the dead end of a disabled
   * Continue button with no explanation.
   */
  function onColorToggle(input, color) {
    var max = state.colorMode === 'single' ? 1 : (state.blendCount || 0);

    if (input.checked) {
      if (state.colors.length >= max) {
        if (max === 1) {
          // Single color: replace the previous choice and SAY SO.
          var prev = state.colors[0];
          state.colors = [color.id];
          $$('[data-pb-color]').forEach(function (c) {
            if (c.value !== color.id) c.checked = false;
          });
          if (prev) announce(color.name + ' selected, replacing your previous choice.');
        } else {
          // Limit reached — explain rather than silently refusing.
          input.checked = false;
          announce('You can pick ' + max + ' colors. Deselect one first.', true);
          return;
        }
      } else {
        state.colors.push(color.id);
      }
    } else {
      state.colors = state.colors.filter(function (id) { return id !== color.id; });
    }

    renderColorGuidance();
    renderBlendPreview();
    render();
  }

  function renderColorGuidance() {
    var max = state.colorMode === 'single' ? 1 : (state.blendCount || 0);
    var legend = $('[data-pb-colors-legend]');
    var counter = $('[data-pb-color-counter]');
    var hint = $('[data-pb-color-hint]');
    if (!max) return;

    if (legend) legend.textContent = max === 1 ? 'Pick your color' : 'Pick your ' + max + ' colors';
    if (counter) counter.textContent = state.colors.length + ' of ' + max;

    if (hint) {
      var remaining = max - state.colors.length;
      hint.textContent = remaining > 0
        ? 'Pick ' + remaining + ' more color' + (remaining === 1 ? '' : 's') + ' to continue'
        : 'All set — ' + max + ' of ' + max + ' selected';
    }
  }

  /**
   * D-035 tiled-band rendering. Still used for BOTH single-colour selection
   * (unconditionally — D-046 is additive for blends only, see the header note
   * on renderBlendPreview) and the Step 5 summary swatch (always, regardless
   * of blend size — that thumbnail is a small recap, not the interactive Step
   * 3 showcase, and does not warrant its own async preload/paint cycle).
   */
  function renderTiledSwatch(sel, chosen, max) {
    var sw = $(sel);
    if (!sw) return;

    sw.innerHTML = '';
    sw.classList.remove('pb__preview-swatch--confetti');
    sw.classList.toggle('pb__preview-swatch--tiled', chosen.length > 1);

    chosen.forEach(function (c) {
      var band = document.createElement('img');
      band.className = 'pb__preview-band';
      band.src = c.swatch;
      band.alt = '';
      band.loading = 'lazy';
      band.decoding = 'async';
      band.setAttribute('aria-hidden', 'true');
      sw.appendChild(band);
    });

    sw.setAttribute('aria-label', chosen.length === 1
      ? chosen[0].name
      : 'An even blend of ' + chosen.map(function (c) { return c.name; }).join(', '));
  }

  /**
   * Monotonic token identifying the newest confetti-preview request. Mirrors
   * freightRequestToken exactly: PBConfettiRender.preload() does no DOM work
   * and knows nothing about sequencing, so a reply that arrives after the
   * customer has toggled colours again must be discarded by the CALLER, not
   * by the render module.
   */
  var confettiRequestToken = 0;
  var confettiPaletteWarmed = false;

  /**
   * D-046 — confetti-piece scatter preview for a BLEND (2–4 colours), ported
   * from the legacy build-your-confetti-mix page. Replaces D-035's tiled
   * bands for this case.
   *
   * Item 13 (2026-09-11) — now paints BOTH the Step 3 live preview AND the
   * Step 5 summary thumbnail from the SAME preload() call (one cache check,
   * one set of already-warm images — see D-046's own preload/warm design;
   * this reuses it rather than duplicating a second fetch cycle). Feasible
   * specifically because Color always precedes Summary in the wizard
   * (stepComplete gating), so by the time a customer could reach Step 5 in
   * this session, these exact images are already cached from Step 2 — no
   * added latency in the real funnel, only in the (harmless) case of a
   * direct #goto jump before ever visiting Step 2.
   */
  function renderConfettiPreview(chosen) {
    var mainSw = $('[data-pb-preview-swatch]');
    var summarySw = $('[data-pb-preview-swatch-summary]');
    if (!mainSw && !summarySw) return;

    var label = 'An even blend of ' + chosen.map(function (c) { return c.name; }).join(', ');

    [mainSw, summarySw].forEach(function (sw) {
      if (!sw) return;
      // Undo the single-color hide() above (2026-09-16) — a blend always
      // shows its own swatch, regardless of what the previous selection was.
      sw.hidden = false;
      sw.classList.add('pb__preview-swatch--confetti');
      sw.classList.remove('pb__preview-swatch--tiled');
      sw.setAttribute('aria-label', label);
      /**
       * Clear immediately, synchronously — before the async preload
       * resolves. Without this, switching from single-color into a blend
       * would leave the PREVIOUS content visible for however long the piece
       * images take to load (typically near-instant on a warm cache, up to
       * ~1.4s cold — see D-046). A brief blank panel reads as "updating";
       * stale content from the wrong mode does not.
       */
      sw.innerHTML = '';
    });

    var colorKeys = chosen.map(function (c) { return c.id; });
    var token = ++confettiRequestToken;

    window.PBConfettiRender.preload(colorKeys).then(function (loaded) {
      if (token !== confettiRequestToken) return; // superseded — discard
      if (mainSw) window.PBConfettiRender.paint(mainSw, colorKeys, loaded);
      /**
       * The summary swatch is very likely HIDDEN right now (Step 3/Color
       * always precedes Step 5 in the wizard) — paint() measures the
       * container's REAL pixel size via getBoundingClientRect(), which is
       * 0×0 for a hidden element, so this call harmlessly renders nothing
       * yet. goToStep()'s repaintSummaryConfetti() repaints it — for free,
       * from the same cached `loaded` images this preload() just resolved
       * into cache — the moment Step 5 actually becomes visible.
       */
      if (summarySw) window.PBConfettiRender.paint(summarySw, colorKeys, loaded);

      // Warm the rest of the palette ONCE, after the first real render, so a
      // customer trying other combinations sees them appear instantly. Idle
      // time only — this must never compete with anything the customer is
      // actively waiting on.
      if (!confettiPaletteWarmed) {
        confettiPaletteWarmed = true;
        var warm = function () { window.PBConfettiRender.warmPalette(); };
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(warm, { timeout: 4000 });
        } else {
          setTimeout(warm, 1500);
        }
      }
    });
  }

  /**
   * Item 13 (2026-09-11) — repaint the Step 5 summary confetti swatch once
   * its container actually has real layout (called from goToStep() on
   * arrival at Step 5). See the note on renderConfettiPreview above for why
   * this is needed at all: a paint attempted while Step 5 was hidden
   * measures a 0×0 box and renders nothing. Cheap: preload() for
   * already-cached colors resolves from the in-memory cache, no network.
   */
  function repaintSummaryConfetti() {
    var summarySw = $('[data-pb-preview-swatch-summary]');
    if (!summarySw || !summarySw.classList.contains('pb__preview-swatch--confetti')) return;

    var max = state.colorMode === 'single' ? 1 : (state.blendCount || 0);
    if (max <= 1 || state.colors.length !== max) return;

    var chosen = state.colors.map(function (id) {
      return cfg.color.catalog.filter(function (c) { return c.id === id; })[0];
    }).filter(Boolean);
    var colorKeys = chosen.map(function (c) { return c.id; });

    window.PBConfettiRender.preload(colorKeys).then(function (loaded) {
      window.PBConfettiRender.paint(summarySw, colorKeys, loaded);
    });
  }

  function renderBlendPreview() {
    var wrap = $('[data-pb-blend-preview]');
    var max = state.colorMode === 'single' ? 1 : (state.blendCount || 0);
    if (!wrap || !max || state.colors.length !== max) {
      hide(wrap);
      confettiRequestToken++; // discard any in-flight confetti preload — nothing to paint
      return;
    }

    var chosen = state.colors.map(function (id) {
      return cfg.color.catalog.filter(function (c) { return c.id === id; })[0];
    }).filter(Boolean);

    var blend = cfg.color.blends.filter(function (b) { return b.count === max; })[0];
    var share = blend ? blend.share : 100;

    var heading = $('[data-pb-preview-heading]');
    if (heading) heading.textContent = max === 1 ? 'Your color' : 'Your blend';

    /**
     * D-035 tiled bands for a single color (max===1). D-046 confetti scatter
     * for a blend (max>1) — item 13 (2026-09-11) extends this to the Step 5
     * summary thumbnail too, not just this step's own live preview (previously
     * always tiled regardless of mode; see the note on renderConfettiPreview
     * for why a blend now gets the real scatter visual in both places, painted
     * together from one preload() call).
     *
     * 2026-09-16 (Product Owner request) — this step's OWN swatch photo
     * (`[data-pb-preview-swatch]`) is removed for a single color: hidden
     * rather than painted, so only the "YOUR COLOR" label + name caption
     * remain. Scoped to this element only — the Step 5 summary thumbnail
     * (`[data-pb-preview-swatch-summary]`) still renders exactly as before,
     * and a Mix Your Own blend (the `else` branch) is completely unaffected,
     * since renderConfettiPreview() paints both swatches unconditionally.
     */
    if (max === 1) {
      confettiRequestToken++; // leaving confetti mode discards any in-flight preload
      hide($('[data-pb-preview-swatch]'));
      renderTiledSwatch('[data-pb-preview-swatch-summary]', chosen, max);
    } else {
      renderConfettiPreview(chosen);
    }

    var caption = $('[data-pb-preview-caption]');
    if (caption) {
      caption.textContent = chosen.map(function (c) {
        return c.name + (max === 1 ? '' : ' ' + share + '%');
      }).join(' · ');
    }

    show(wrap);
  }

  /* =====================================================================
   * STEP 4 — Shipping and the freight state machine
   * =================================================================== */
  function wireStep4() {
    var zip = $('[data-pb-field="zip"]');
    if (zip) {
      zip.addEventListener('input', function () {
        state.zip = zip.value.trim() || null;
        invalidateFreight('zip');
        render();
      });
    }

    $$('[data-pb-delivery]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        state.deliveryType = input.value;
        // Item 11 (2026-09-11) — liftgate no longer varies by delivery type
        // (state.liftgate is a permanent 'yes'), so there is nothing left to
        // reapply here. Freight is still destination-dependent, so a fetched
        // quote is still stale.
        invalidateFreight('delivery');
        render();
      });
    });

    /**
     * Item 12 (2026-09-11) — was a fixed 2-element array (`$` returns only
     * the FIRST match), which silently wired only one "Try again" button
     * even when more than one exists (now true: the D-004 estimated-range
     * retry AND the invalid_zip first-attempt retry). `$$` wires all of them.
     */
    [$('[data-pb-quote]')].concat($$('[data-pb-quote-retry]')).forEach(function (btn) {
      if (btn) btn.addEventListener('click', fetchFreight);
    });

    /**
     * Item 12 (2026-09-11) — the invalid_zip escape hatch's own Save Project
     * button. A customer stuck here can never reach Step 5's through normal
     * navigation (Continue is disabled — see stepComplete(4)'s note), so this
     * is a genuine second entry point into the SAME save flow, not a
     * decoration — see the outputEl note on submitSaveProject.
     */
    var zipErrorSave = $('[data-pb-zip-error-save]');
    var zipErrorSaveOutput = $('[data-pb-zip-error-save-output]');
    if (zipErrorSave) {
      zipErrorSave.addEventListener('click', function () {
        submitSaveProject(zipErrorSave, zipErrorSaveOutput);
      });
    }
  }

  /**
   * SH-13 / OQ-25 — anything that changes weight or destination invalidates a
   * fetched quote. The panel shows freight as "—" while stale rather than
   * displaying a number we know is wrong.
   */
  /**
   * Monotonic counter identifying the newest freight request. Any reply whose
   * token no longer matches is stale and must be discarded (see fetchFreight).
   */
  var freightRequestToken = 0;

  function invalidateFreight(reason) {
    // Bump unconditionally: an in-flight request must be abandoned even when the
    // visible status is already 'none', or its reply could resurrect a stale quote.
    freightRequestToken++;
    if (state.freight.status === 'none') return;
    state.freight = { status: 'none', invalidatedBy: reason };
    renderFreightStates();
    announce('Your delivery details changed, so we need to get a fresh freight rate.');
  }

  function fetchFreight() {
    /**
     * Item 1 (2026-09-11) — flat-fee orders never call R+L. This is a
     * synchronous local lookup, not a network request: the table is public,
     * non-negotiated, ZIP-independent data (same category as material
     * pricing already computed client-side), so there is nothing to fetch.
     * SH-18 is unaffected — this number is never trusted for the ACTUAL
     * charge; checkout.js recomputes it server-side from the identical
     * CALC.flatFeeForCubicFeet(), same discipline as every other figure.
     */
    var billed = window.PBCalculator.derive(state).cubicFeet;
    if (window.PBCalculator.isFlatFeeEligible(billed)) {
      freightRequestToken++; // invalidate any still-in-flight R+L request from before dimensions shrank
      var flatAmount = window.PBCalculator.flatFeeForCubicFeet(billed);
      state.freight = flatAmount !== null
        ? { status: 'quoted', amount: flatAmount, transitDays: null, serviceCode: 'FLAT_UPS', flatFee: true }
        : { status: 'estimated', amount: null, transitDays: null, range: null, reason: 'flat_fee_table_gap', flatFee: true };
      renderFreightStates();
      render();
      announce(flatAmount !== null
        ? 'Your UPS shipping cost is ready.'
        : 'We could not get a shipping cost. Please try again, or talk to a specialist.', flatAmount === null);
      return;
    }

    state.freight = { status: 'loading' };
    renderFreightStates();
    render();
    announce('Getting your freight rate.');

    /**
     * REAL (Sprint 9, D-043) — calls the backend through the App Proxy.
     * SH-18: the amount comes from the SERVER and is never computed here.
     *
     * PBFreightClient never rejects — every failure resolves to
     * status:'estimated' with a reason — so there is no .catch(). A rejection
     * here would leave the customer stuck on the loading state forever, which
     * is the one outcome D-004 forbids.
     *
     * The request is guarded against races: only the most recent one may write
     * to state. Without this, a slow quote for an OLD destination could land
     * after a fast quote for the current one and overwrite it with a wrong
     * amount — SH-13 invalidates on change, but an in-flight request predates
     * the invalidation.
     */
    var token = ++freightRequestToken;

    window.PBFreightClient.fetchFreight(state).then(function (result) {
      if (token !== freightRequestToken) return; // superseded — discard silently

      state.freight = result;

      /**
       * Item 12 (2026-09-11) — track CONSECUTIVE invalid_zip results. Any
       * other outcome (quoted / estimated / oversized) means the ZIP itself
       * was fine, so the count resets — this is specifically "how many times
       * in a row has R+L rejected the ZIP itself", not a lifetime counter.
       */
      state.zipErrorCount = result.status === 'invalid_zip' ? state.zipErrorCount + 1 : 0;

      renderFreightStates();
      render();

      if (result.status === 'quoted') {
        announce('Freight rate received.');
      } else if (result.status === 'oversized') {
        // D-070 — a distinct announcement: this is not a carrier outage.
        announce('This order is too large to quote automatically. You can still continue — we will follow up with your exact freight cost.', true);
      } else if (result.status === 'invalid_zip') {
        // Item 12 — a customer-fixable problem, not a carrier outage, so it
        // gets its own announcement rather than the generic D-004 one below.
        var zipField = $('[data-pb-field="zip"]');
        if (state.zipErrorCount >= 2) {
          announce('That ZIP still doesn’t look right. Talk to a specialist, or save your project and come back to it.', true);
        } else {
          announce('That ZIP code doesn’t look right. Please check it and try again.', true);
          if (zipField) zipField.focus();
        }
      } else {
        // D-004 — this changes what the customer is agreeing to, so it interrupts.
        announce('We could not reach R+L. An estimated freight range is shown instead. You can still continue.', true);
      }
    });
  }

  /**
   * D-070 — `oversized` is a first-class freight status, not a special case
   * of `estimated`. A shipment over R+L's 16,000 lb ceiling is a KNOWN,
   * PERMANENT limit, not a carrier outage — the generic "couldn't reach R+L,
   * try again" copy would be actively wrong here (we may never have called
   * R+L at all — the server's own guard fires first — and retrying an
   * over-ceiling shipment fails deterministically, every time). It gets its
   * own state block (`data-pb-freight="oversized"`), separate from
   * `data-pb-freight="estimated"`.
   */
  function renderFreightStates() {
    ['loading', 'quoted', 'estimated', 'oversized', 'invalid_zip'].forEach(function (s) {
      toggle($('[data-pb-freight="' + s + '"]'), state.freight.status === s);
    });
    // The specialist nudge applies to both "not a confirmed live quote" cases.
    // NOT invalid_zip — that state carries its own, more specific specialist/
    // save routing once it escalates (item 12), rather than this generic one.
    toggle($('[data-pb-specialist-nudge]'),
      state.freight.status === 'estimated' || state.freight.status === 'oversized');

    /**
     * Item 12 (2026-09-11) — two sub-messages within the SAME invalid_zip
     * block: first attempt prompts a re-entry, a second consecutive one
     * stops suggesting "try again" (it is no longer a plausible typo) and
     * routes to a specialist or Save Project instead.
     */
    if (state.freight.status === 'invalid_zip') {
      toggle($('[data-pb-zip-error-first]'), state.zipErrorCount < 2);
      toggle($('[data-pb-zip-error-repeat]'), state.zipErrorCount >= 2);
    }

    /**
     * Item 1 (2026-09-11) — carrier-aware copy. `state.freight.flatFee` is
     * only ever true on the synchronous local-lookup path in fetchFreight(),
     * so this can never mislabel a real R+L quote or vice versa.
     */
    var isFlat = !!state.freight.flatFee;
    setText('out', 'freightLoadingLabel', isFlat
      ? 'Getting your UPS shipping cost…'
      : 'Getting your live rate from R+L Carriers…');
    setText('out', 'freightTag', isFlat ? 'Flat rate via UPS' : 'Live rate from R+L Carriers');
    // No liftgate on a UPS parcel shipment — the whole reason it exists (item 11).
    toggle($('[data-pb-liftgate-meta]'), !isFlat);
    // Transit days are never returned for the flat-fee table (no carrier
    // response to read them from) — hide the line rather than show "—".
    toggle($('[data-pb-transit-meta]'), !isFlat);

    var quoteBtn = $('[data-pb-quote]');
    if (quoteBtn) {
      var ready = !!state.zip && !!state.deliveryType && !!state.liftgate;
      quoteBtn.disabled = !ready || state.freight.status === 'loading';
      toggle(quoteBtn, state.freight.status === 'none' || state.freight.status === 'loading');
    }

    // Wireframe 04-shipping.md State D — the button says what the customer is
    // actually agreeing to. Proceeding on a labelled estimate is explicitly
    // allowed (SH-05, SH-08), but it should not read as a confirmed quote.
    var nextLabel = $('[data-pb-next-label]');
    if (nextLabel) {
      nextLabel.textContent = state.freight.status === 'oversized'
        ? 'Continue — we’ll follow up'
        : state.freight.status === 'estimated'
          ? 'Continue with estimate'
          : 'Continue';
    }
  }

  /* =====================================================================
   * STEP 5 — Summary
   * =================================================================== */
  /**
   * Send the project to the SAVE endpoint and report the truth about it.
   *
   * Mirrors submitSpecialist's shape deliberately: same SH-18-style discipline
   * (raw inputs only, no derived price), same never-hardcode-the-blocker
   * lesson from D-045, same three-outcome honesty. Kept as a separate function
   * rather than generalised with submitSpecialist because the two have
   * different payloads, different success semantics (a project has no "form"
   * to hide) and Email deliberately stays unwired — a shared abstraction would
   * either grow a branch for the thing that does not exist yet, or tempt
   * someone to bolt Email onto it before OQ-16/OQ-17 are actually resolved.
   */
  /**
   * `outputEl` (item 12, 2026-09-11) — optional override for where the
   * result message renders. Defaults to Step 5's own status paragraph
   * (unchanged behaviour for the Step 5 Save button). The invalid_zip escape
   * hatch in Step 4 (a customer stuck there can never REACH Step 5 through
   * normal navigation — Continue stays disabled, per stepComplete(4)) passes
   * its own local paragraph instead, so the confirmation is visible on
   * whichever step the customer actually clicked Save from.
   */
  function submitSaveProject(button, outputEl) {
    var cfg = window.PBConfig;
    var notBuilt = outputEl || $('[data-pb-notbuilt]');
    if (button) button.disabled = true;
    announce('Saving your project.');

    var payload = {
      project: {
        shape: state.shape, length: state.length, width: state.width,
        diameter: state.diameter, depth: state.depth, packaging: state.packaging,
        colorMode: state.colorMode, colors: state.colors, zip: state.zip
      }
    };

    fetch(cfg.backend.saveEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        return { ok: res.ok, data: data };
      });
    }).catch(function () {
      return { ok: false, data: {} };
    }).then(function (r) {
      if (button) button.disabled = false;

      var data = r.data || {};

      /**
       * REAL as of P-002 (2026-08-28) — a genuine token comes back and the
       * customer needs an actual, usable link, not just a congratulatory
       * message with nothing to act on. The Product-Owner-approved sentence
       * (2026-08-20) is kept EXACTLY as approved, with the link appended
       * after it rather than replacing it — this is new functionality on top
       * of existing approved copy, not a rewrite of it. Built via DOM methods
       * rather than an HTML string, even though the token's own character set
       * (hex + hyphens, see generateProjectToken()) is already URL-safe — a
       * link the customer might paste into an email deserves the same
       * defensive habit as anything else user-facing.
       */
      if (r.ok && data.saved === true && data.token) {
        var url = window.location.origin + cfg.savedProject.reopenPath + '?id=' + encodeURIComponent(data.token);
        if (notBuilt) {
          notBuilt.textContent = "Saved — you're all set. Come back anytime using this link: ";
          var link = document.createElement('a');
          link.href = url;
          link.textContent = url;
          notBuilt.appendChild(link);
          show(notBuilt);
        }
        // The live-region announcement stays short — reading a 40-character
        // token aloud is not useful; the visible link is there to copy.
        announce('Saved. A link to reopen your project is shown below.', true);
        return;
      }

      var text;
      if (r.ok && data.saved === false) {
        // Honest, and pulled from the SERVER's own reason rather than a
        // hardcoded client string — the exact lesson D-045 exists to enforce.
        var blocked = data.blockedBy ? ' (' + data.blockedBy + ')' : '';
        text = 'Saving is not available yet' + blocked + '.' +
          (data.detail ? ' ' + data.detail : '') + ' Nothing has been saved.';
      } else {
        text = 'Something went wrong saving your project, and nothing has been saved. Please try again.';
      }

      if (notBuilt) { notBuilt.textContent = text; show(notBuilt); }
      announce(text, true);
    });
  }

  /**
   * D-047 (Sprint 10) — the single /checkout entry point.
   *
   * Contact is OPTIONAL here: the server needs it only when it decides the
   * order routes to the Draft Order path, and that decision is the server's
   * to make (see checkout.js's own security note — the branch cannot be
   * client-chosen). This function does not decide the path either; it just
   * asks and reports what came back.
   */
  function buildCheckoutPayload(contact) {
    var payload = {
      shape: state.shape, length: state.length, width: state.width, diameter: state.diameter,
      depth: state.depth, packaging: state.packaging,
      colorMode: state.colorMode, colors: state.colors,
      zip: state.zip, deliveryType: state.deliveryType, liftgate: state.liftgate
    };
    if (contact) payload.contact = contact;
    return payload;
  }

  function submitCheckout(contact) {
    return fetch(cfg.backend.checkoutEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(buildCheckoutPayload(contact))
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        return { ok: res.ok, status: res.status, data: data };
      });
    }).catch(function () {
      return { ok: false, status: 0, data: {} };
    });
  }

  /**
   * Add the SERVER-TRUSTED cart items to Shopify's own native cart and hand
   * off to real Shopify checkout. `cart.items` comes straight from the
   * /checkout response — this function never computes a quantity or looks up
   * a variant itself, extending SH-18's discipline to Path 1's material and
   * freight-unit quantities, not just the freight amount.
   *
   * `cart.attributes` (D-075) is passed through unmodified too — it carries
   * the playground_builder marker the orders/create webhook uses to tag the
   * resulting Order the same way Path 2's Draft Orders already are.
   */
  function addToNativeCartAndRedirect(cart, onFail) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cart.items, attributes: cart.attributes || {} })
    }).then(function (res) {
      if (!res.ok) throw new Error('cart_add_failed');
      window.location.href = '/checkout';
    }).catch(function () {
      onFail('We could not add your project to cart. Please try again, or talk to a specialist.');
    });
  }

  /**
   * Dispatch a /checkout response. Shared by the direct "Checkout" click
   * (under threshold, no contact collected) and the invoice-request overlay's
   * submit (at/above threshold, contact collected) — one place interprets
   * `path`, so the two call sites cannot drift into disagreeing about what a
   * given response means.
   */
  function handleCheckoutResponse(r, opts) {
    var onNotReady = opts.onNotReady;
    var onDraftOutcome = opts.onDraftOutcome;
    var onNeedsContact = opts.onNeedsContact;

    if (!r.ok && r.status === 0) {
      onNotReady('Something went wrong reaching checkout. Please try again, or talk to a specialist.');
      return;
    }
    var data = r.data || {};

    if (data.path === 'declined') {
      // Freight could not be confirmed at checkout time (D-047 — checkout
      // must not guess a price, unlike Step 4's preview estimate).
      onNotReady("We couldn't confirm your shipping cost just now. Please try again, or talk to a specialist.");
      return;
    }

    if (data.path === 'native') {
      if (data.ready) {
        addToNativeCartAndRedirect(data.cart, onNotReady);
      } else {
        // D-047's known pending prerequisite — the freight-unit product does
        // not exist yet. Honest, not a generic error.
        onNotReady('Checkout for this order size is not available yet. Please talk to a specialist and we will get this sorted out for you.');
      }
      return;
    }

    if (data.path === 'draft') {
      if (onNeedsContact) {
        // The direct button was clicked (no contact collected) but the server
        // determined this order needs the Draft Order path. Rare — e.g. the
        // total shifted between what the customer last saw and what the
        // server just recomputed. Reopen the invoice form rather than fail.
        onNeedsContact();
        return;
      }
      onDraftOutcome(data);
      return;
    }
  }

  function wireStep5() {
    var notBuilt = $('[data-pb-notbuilt]');

    function explain(msg) {
      if (!notBuilt) return;
      notBuilt.textContent = msg;
      show(notBuilt);
    }

    /**
     * D-047 (Sprint 10) — genuinely wired. The button's own label already
     * reflects which path is expected (renderSummary, driven by the same real
     * total the customer can see on screen) — this handler acts on what the
     * SERVER decides, which is authoritative regardless of the label.
     */
    var checkout = $('[data-pb-checkout]');
    if (checkout) {
      checkout.addEventListener('click', function () {
        var currentTotal = window.PBCalculator.derive(state).total;
        var overThreshold = currentTotal !== null && currentTotal >= cfg.checkout.draftOrderThreshold;

        if (overThreshold) {
          openCheckoutOverlay(checkout);
          return;
        }

        checkout.disabled = true;
        hide(notBuilt);
        announce('Getting your order ready.');

        submitCheckout(null).then(function (r) {
          checkout.disabled = false;
          handleCheckoutResponse(r, {
            onNotReady: function (msg) { explain(msg); announce(msg, true); },
            onNeedsContact: function () { openCheckoutOverlay(checkout); }
          });
        });
      });
    }
    /**
     * SAVE PROJECT — genuinely wired (2026-08-20), unlike Email below.
     *
     * D-003's model is ANONYMOUS: no email, no account, just the project state
     * we already hold. So there is nothing left to build in the UI to call the
     * real endpoint — it can be wired now, and gated on the server's own
     * `saved` flag exactly like the specialist overlay (D-045).
     */
    var save = $('[data-pb-save]');
    if (save) {
      save.addEventListener('click', function () { submitSaveProject(save); });
    }

    /**
     * EMAIL PROJECT — genuinely wired (D-061, 2026-09-03).
     *
     * OQ-16 (platform), OQ-17 (consent copy), and P-002 (save/resume data
     * model) are all resolved now, so the two things that kept this a stub
     * are gone. Opens the same accessible overlay pattern as Checkout/
     * Specialist rather than sending anything from this click handler
     * directly — the overlay is where the email + consent fields actually
     * live (D-003 keeps Save Project itself anonymous).
     */
    var email = $('[data-pb-email]');
    if (email) {
      email.addEventListener('click', function () { openProjectEmailOverlay(email); });
    }
  }

  /* =====================================================================
   * EMAIL PROJECT OVERLAY — D-061, Sprint 12/D-057
   *
   * Same accessible modal machinery as the checkout/specialist overlays.
   * This is the ONE place in the builder that asks for an email — Save
   * Project stays anonymous (D-003) — because sending a link TO an address
   * is the entire point of this action, not an incidental capture point.
   * =================================================================== */
  function openProjectEmailOverlay(trigger) {
    var overlay = $('[data-pb-project-email-overlay]');
    if (!overlay) return;

    openOverlay(overlay, trigger, function () {
      var form = $('[data-pb-project-email-form]');
      var outcome = $('[data-pb-project-email-outcome]');
      if (form) show(form);
      if (outcome) hide(outcome);
    });
  }

  function wireProjectEmailOverlay() {
    var form = $('[data-pb-project-email-form]');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var emailInput = form.querySelector('#pb-pe-email');
      var errEl = $('[data-pb-error="pe-email"]');
      var emailVal = emailInput ? emailInput.value.trim() : '';
      var badEmail = !emailVal || emailVal.indexOf('@') === -1;

      if (badEmail) {
        if (errEl) {
          errEl.textContent = !emailVal ? 'Please enter an email address' : 'That email address does not look right';
          show(errEl);
        }
        if (emailInput) emailInput.setAttribute('aria-invalid', 'true');
        return;
      }
      if (errEl) hide(errEl);
      if (emailInput) emailInput.removeAttribute('aria-invalid');

      var consentInput = form.querySelector('#pb-pe-consent');
      var marketingConsent = Boolean(consentInput && consentInput.checked);

      var submitBtn = form.querySelector('[data-pb-project-email-submit]');
      if (submitBtn) submitBtn.disabled = true;
      announce('Sending your project.');

      submitProjectEmail(emailVal, marketingConsent).then(function (r) {
        if (submitBtn) submitBtn.disabled = false;
        showProjectEmailOutcome(r.data || {});
      });
    });
  }

  /**
   * SH-18 discipline extended to Email Project — raw inputs only, exactly
   * the same fields Save Project already sends (state.*, never a derived
   * price/weight); the server recomputes everything, including what gets
   * stored in the project record.
   */
  function submitProjectEmail(contactEmail, marketingConsent) {
    var payload = {
      contactEmail: contactEmail,
      marketingConsent: marketingConsent,
      project: {
        shape: state.shape, length: state.length, width: state.width,
        diameter: state.diameter, depth: state.depth, packaging: state.packaging,
        colorMode: state.colorMode, colors: state.colors, zip: state.zip
      }
    };

    return fetch(cfg.backend.emailEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        return { ok: res.ok, data: data };
      });
    }).catch(function () {
      return { ok: false, data: {} };
    });
  }

  /**
   * Mirrors showCheckoutOutcome's discipline: the reason comes from the
   * SERVER, never a hardcoded client string (D-045).
   *
   * D-064 — simplified back to the same sent/blockedBy/generic-failure shape
   * every other outcome panel in this file uses. D-063's `requiresConsent`
   * branch is gone: the send no longer goes through Shopify Flow at all
   * (see project.js), so consent no longer gates whether this can send —
   * `sent: true` now means the backend's own Resend call actually succeeded,
   * not a hope that a Flow would eventually act on a tag.
   */
  function showProjectEmailOutcome(data) {
    var form = $('[data-pb-project-email-form]');
    var heading = $('[data-pb-project-email-outcome-heading]');
    var body = $('[data-pb-project-email-outcome-body]');
    var outcome = $('[data-pb-project-email-outcome]');

    var title;
    var text;

    if (data.sent === true) {
      title = 'Sent';
      text = "Check your email for your project — it's on its way.";
    } else if (data.blockedBy) {
      title = 'Not sent yet';
      text = 'Emailing your project is not available yet (' + data.blockedBy + ').' +
        (data.detail ? ' ' + data.detail : '') + ' Please talk to a specialist instead.';
    } else {
      title = 'Could not send';
      text = (data.detail || 'Something went wrong, and nothing has been sent.') +
        ' Please try again, or talk to a specialist.';
    }

    if (form) hide(form);
    if (outcome) show(outcome);
    if (heading) { heading.textContent = title; heading.focus(); }
    if (body) body.textContent = text;
    announce(title + '. ' + text, true);
  }

  /* =====================================================================
   * DRAFT ORDER CHECKOUT OVERLAY — D-047, Sprint 10; redesigned 2026-10-14
   *
   * Opened only when the pre-tax total is at/above the Draft Order threshold
   * (or when the server disagrees with the client's own read of that, per
   * handleCheckoutResponse's onNeedsContact). Reuses the SAME accessible modal
   * machinery as the specialist overlay (openOverlay/closeOverlay were
   * generalised for exactly this) rather than a second implementation.
   * =================================================================== */
  function openCheckoutOverlay(trigger) {
    var overlay = $('[data-pb-checkout-overlay]');
    if (!overlay) return;

    openOverlay(overlay, trigger, function () {
      var form = $('[data-pb-checkout-form]');
      var outcome = $('[data-pb-checkout-outcome]');
      var redirecting = $('[data-pb-checkout-redirecting]');
      if (form) show(form);
      if (outcome) hide(outcome);
      if (redirecting) hide(redirecting);

      var totalLine = $('[data-pb-checkout-total-line]');
      if (totalLine) {
        var d = window.PBCalculator.derive(state);
        totalLine.textContent = d.total !== null
          ? 'Your total: ' + fmtMoney(d.total) + (d.totalIncludesTax ? '' : ' + tax, calculated at checkout')
          : '';
      }
    });
  }

  function wireCheckoutOverlay() {
    var form = $('[data-pb-checkout-form]');
    if (!form) return;

    var retryBtn = $('[data-pb-checkout-outcome-retry]');
    if (retryBtn) {
      retryBtn.addEventListener('click', function () {
        hide($('[data-pb-checkout-outcome]'));
        show(form);
        var emailInput = form.querySelector('#pb-co-email');
        if (emailInput) emailInput.focus();
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var emailInput = form.querySelector('#pb-co-email');
      var errEl = $('[data-pb-error="co-email"]');
      var email = emailInput ? emailInput.value.trim() : '';
      var badEmail = !email || email.indexOf('@') === -1;

      if (badEmail) {
        if (errEl) {
          errEl.textContent = !email ? 'Please enter an email address' : 'That email address does not look right';
          show(errEl);
        }
        if (emailInput) emailInput.setAttribute('aria-invalid', 'true');
        return;
      }
      if (errEl) hide(errEl);
      if (emailInput) emailInput.removeAttribute('aria-invalid');

      var contact = {
        name: (form.querySelector('#pb-co-name') || {}).value || '',
        email: email,
        phone: (form.querySelector('#pb-co-phone') || {}).value || ''
      };

      var submitBtn = form.querySelector('[data-pb-checkout-submit]');
      if (submitBtn) submitBtn.disabled = true;
      announce('Taking you to checkout.');

      submitCheckout(contact).then(function (r) {
        if (submitBtn) submitBtn.disabled = false;
        handleCheckoutResponse(r, {
          onNotReady: function (msg) {
            showCheckoutOutcome({ path: 'draft', redirected: false, detail: msg });
          },
          onDraftOutcome: function (data) { showCheckoutOutcome(data); }
          // No onNeedsContact here — we ARE the contact form. If the server
          // somehow still asks for it (shouldn't happen once email is sent),
          // onDraftOutcome's honest-failure branch below covers it via
          // whatever reason/detail the server supplies.
        });
      });
    });
  }

  /**
   * Render the outcome of a checkout submission. Mirrors showSpecialistOutcome's
   * discipline: the reason comes from the SERVER, never a hardcoded client
   * string.
   *
   * Redesigned 2026-10-14 ("Simplify Path 2 checkout"): the common case is no
   * longer a screen at all — `data.redirected`/`invoiceUrl` sends the browser
   * straight to Shopify checkout. This function still understands the OLD
   * (`invoiceSent`) response shape too, because `PATH2_LEGACY_INVOICE_EMAIL`
   * (backend/wrangler.toml) can be flipped independently of this theme push —
   * see checkout.js's handleDraftOrderPath docstring. Never remove the legacy
   * branch by assuming the flag has already been flipped; check it, or
   * better, leave this dual handling in place since it costs nothing once
   * both modes agree on the same `invoiceUrl` field.
   *
   * A "someone else is paying" forward-by-email path was designed and
   * briefly built here (2026-10-14), then dropped the next day — the Product
   * Owner confirmed the builder is usually the payer. If you're looking for
   * it, it's gone, not merely unreachable.
   */
  function showCheckoutOutcome(data) {
    var form = $('[data-pb-checkout-form]');
    var heading = $('[data-pb-checkout-outcome-heading]');
    var body = $('[data-pb-checkout-outcome-body]');
    var outcome = $('[data-pb-checkout-outcome]');
    var redirecting = $('[data-pb-checkout-redirecting]');
    var retryBtn = $('[data-pb-checkout-outcome-retry]');
    var specialistBtn = $('[data-pb-checkout-outcome-specialist]');

    // Covers both the new shape (data.redirected) and the legacy shape
    // (data.invoiceSent) — see the docstring above.
    var readyToRedirect = (data.redirected === true || data.invoiceSent === true) && data.invoiceUrl;
    if (readyToRedirect) {
      if (form) hide(form);
      if (outcome) hide(outcome);
      if (redirecting) { show(redirecting); var h = redirecting.querySelector('h3'); if (h) h.focus(); }
      announce('Taking you to secure checkout.', true);
      window.setTimeout(function () { window.location.href = data.invoiceUrl; }, 600);
      return;
    }

    var title;
    var text;
    var retryable = false;

    if (data.blockedBy) {
      // OQ-49's pending prerequisite — named honestly, not hidden behind a
      // generic error (same D-045 lesson as the specialist/save flows).
      title = 'Not ready yet';
      text = 'Checkout is not available yet (' + data.blockedBy + ').' +
        (data.detail ? ' ' + data.detail : '') + ' Please talk to a specialist instead.';
    } else if (data.draftOrderCreated) {
      // The order genuinely exists in Shopify — do not tell the customer
      // nothing happened, and do not offer "try again" (that would create a
      // second Draft Order for the same order).
      title = 'Almost there';
      text = 'Your order was created but we could not take you to checkout automatically. ' +
        'A specialist will follow up with your payment link shortly.';
    } else {
      title = 'Something went wrong';
      text = (data.detail || 'We could not reach checkout just now, and nothing has been created.') +
        ' Please try again, or talk to a specialist.';
      retryable = true;
    }

    toggle(retryBtn, retryable);
    toggle(specialistBtn, true);

    if (form) hide(form);
    if (redirecting) hide(redirecting);
    if (outcome) show(outcome);
    if (heading) { heading.textContent = title; heading.focus(); }
    if (body) body.textContent = text;
    announce(title + '. ' + text, true);
  }

  /**
   * D-042 — render the fitted fallback range, or nothing.
   *
   * freightRange is null when no defensible estimate exists (unrecognised ZIP,
   * or a non-LTL lane such as AK/HI/PR). D-004 forbids substituting a guess, so
   * the honest output there is a specialist referral, not a number.
   *
   * Low-confidence bands are hedged in words rather than silently presented as
   * equal to the well-supported ones — the `local` band rests on 11 orders.
   */
  function fmtFreightRange(d, withSuffix) {
    var r = d.freightRange;
    if (!r) return 'We\u2019ll confirm by email';
    var text = fmtMoney(r.low) + ' \u2013 ' + fmtMoney(r.high);
    if (!withSuffix) return text;
    return text + (r.confidence === 'low' ? ' (rough estimate)' : ' (estimated)');
  }

  /**
   * D-070 \u2014 the freight display text used everywhere a chargeable amount
   * isn't available: a live quote wins if we have one; otherwise `oversized`
   * gets its own honest copy (never a range \u2014 see freight.js), and the D-004
   * `estimated` case falls back to fmtFreightRange's fitted range or its own
   * "We'll confirm by email" for a non-defensible lane.
   */
  function freightDisplayText(d, withSuffix) {
    if (d.freightAmount !== null && d.freightAmount !== undefined) return fmtMoney(d.freightAmount);
    if (state.freight.status === 'oversized') return 'Contact us for a custom quote';
    if (state.freight.status === 'estimated') return fmtFreightRange(d, withSuffix);
    return '\u2014';
  }

  function renderSummary(d) {
    var colorNames = state.colors.map(function (id) {
      var c = cfg.color.catalog.filter(function (x) { return x.id === id; })[0];
      return c ? c.name : id;
    });
    var blend = cfg.color.blends.filter(function (b) { return b.count === state.blendCount; })[0];

    setText('sum', 'shape', cfg.dimensions.shapes[state.shape].label);
    setText('sum', 'area', areaLabel(d));
    setText('sum', 'depth', state.depth ? state.depth + ' inches' : '—');
    setText('sum', 'cubicFeet', fmtNum(d.cubicFeet) + ' cu ft');

    setText('sum', 'packaging', d.packagingLabel || '—');
    setText('sum', 'quantity', d.unitCount ? fmtNum(d.unitCount) + ' ' + d.unitNoun : '—');
    setText('sum', 'weight', d.weightLbs ? '~' + fmtNum(d.weightLbs) + ' lbs' : '—');

    setText('sum', 'colorMode', state.colorMode === 'mix'
      ? 'Mix Your Own' + (blend ? ' (' + blend.label + ')' : '')
      : state.colorMode === 'single' ? 'One Color' : '—');
    setText('sum', 'colors', colorNames.length
      ? colorNames.map(function (n) { return blend ? n + ' ' + blend.share + '%' : n; }).join(' · ')
      : '—');

    setText('sum', 'zip', state.zip ? 'ZIP ' + state.zip : '—');
    setText('sum', 'deliveryType', state.deliveryType
      ? capitalise(state.deliveryType) : '—');
    // Item 11 (2026-09-11) — no longer a choice, so no longer a Yes/No
    // answer to report. 'Included' for a freight-truck order, '—' for a
    // flat-fee/UPS order (never involves a liftgate at all).
    setText('sum', 'liftgate', state.freight.flatFee ? '—' : (state.deliveryType ? 'Included' : '—'));

    // D-036 — the composed phrase comes from config; nothing is assembled here.
    setText('sum', 'leadTime', d.leadTimeDisplay);
    setText('sum', 'transit', d.transitDaysLabel);
    // Transit is only known once the carrier has quoted (D-036).
    toggle($('[data-pb-transit-line]'), d.transitDaysLabel !== null);

    // BR-03 — material and freight remain separate rows, always.
    setText('sum', 'materialCost', fmtMoney(d.materialCost));
    var pack = state.packaging ? cfg.packaging[state.packaging] : null;
    setText('sum', 'materialMath', pack
      ? fmtNum(d.cubicFeet) + ' cu ft × ' + fmtMoney(pack.pricePerCubicFoot)
      : '—');

    setText('sum', 'freight', freightDisplayText(d, true));
    // Item 1 (2026-09-11) — carrier-aware note, same rule as renderFreightStates().
    setText('sum', 'freightCarrierNote', state.freight.flatFee
      ? 'UPS, flat rate'
      : state.freight.status === 'quoted' ? 'R+L Carriers, live rate' : 'Estimated');

    // D-037 — subtotal comes from the calculator, already rounded to cents, so
    // the displayed column always sums to the displayed total. Recomputing it
    // here from unrounded parts is what produced the penny mismatches.
    setText('sum', 'subtotal', fmtMoney(d.subtotal));
    /**
     * D-038 (OQ-47) — when no tax estimate is shown, the tax cell carries the
     * placeholder and BOTH the total's label and value say it excludes tax.
     * Driven off the calculator's flag so the label and the number can never
     * disagree about whether tax is included.
     */
    setText('sum', 'tax', d.tax === null ? (d.taxPlaceholder || '—') : fmtMoney(d.tax));
    setText('sum', 'totalLabel', d.totalIncludesTax ? 'Estimated total' : 'Estimated total before tax');
    setText('sum', 'total', d.total === null ? '—'
      : fmtMoney(d.total) + (d.totalIncludesTax ? '' : ' + tax'));
    setText('sum', 'taxNote', d.totalIncludesTax
      ? 'Tax is finalised at checkout once we have your full address.'
      : 'Sales tax is calculated at checkout once we have your full address, and is not included above.');

    /**
     * D-047 (Sprint 10) — the button's label previews which path is expected,
     * driven by the SAME real total the customer can already see above. This
     * is UX guidance only, not the security boundary: /checkout recomputes
     * the total itself and decides the actual path regardless of this label
     * (see the security note on handleCheckout in checkout.js).
     */
    var checkoutBtn = $('[data-pb-checkout]');
    if (checkoutBtn) {
      var overThreshold = d.total !== null && d.total !== undefined &&
        d.total >= cfg.checkout.draftOrderThreshold;
      checkoutBtn.textContent = overThreshold ? 'Request payment link' : 'Checkout';
    }
  }

  /* =====================================================================
   * PROJECT PANEL — grows as the project becomes real (wireframes §7.1)
   * =================================================================== */
  function renderPanel(d) {
    var haveArea = stepComplete(1); // D-028 — shape-aware
    var rows = {
      area: haveArea,
      cubic: haveArea,
      packaging: !!state.packaging,
      quantity: !!state.packaging,
      colour: state.colors.length > 0,
      material: !!state.packaging,
      freight: state.freight.status === 'quoted' || state.freight.status === 'estimated' || state.freight.status === 'oversized',
      total: d.total !== null && d.total !== undefined,
      transit: state.freight.status === 'quoted'
    };
    Object.keys(rows).forEach(function (key) {
      toggle($('[data-pb-row="' + key + '"]'), rows[key]);
    });

    setText('panelout', 'squareFeet', fmtArea(d.squareFeet) + ' sq ft');
    setText('panelout', 'cubicFeet', fmtNum(d.cubicFeet) + ' cu ft');
    setText('panelout', 'packaging', d.packagingLabel || '—');
    setText('panelout', 'quantity', d.unitCount ? fmtNum(d.unitCount) + ' ' + d.unitNoun : '—');

    var colorNames = state.colors.map(function (id) {
      var c = cfg.color.catalog.filter(function (x) { return x.id === id; })[0];
      return c ? c.name : id;
    });
    setText('panelout', 'colour', colorNames.length
      ? (state.colorMode === 'mix' ? 'Mix Your Own · ' : '') + colorNames.join(' / ')
      : '—');

    setText('panelout', 'materialCost', fmtMoney(d.materialCost));
    setText('panelout', 'freight', freightDisplayText(d, false));
    setText('panelout', 'totalLabel', d.totalIncludesTax ? 'Est. total' : 'Est. total before tax');
    setText('panelout', 'total', d.total === null ? '—'
      : fmtMoney(d.total) + (d.totalIncludesTax ? '' : ' + tax'));
    setText('panelout', 'leadTimeQualifier', d.leadTimeQualifier);
    setText('panelout', 'leadTime', d.leadTimeLabel);
    setText('panelout', 'transit', d.transitDaysLabel);

    // Collapsed glance line — the two or three values worth seeing without tapping.
    var glance = $('[data-pb-glance]');
    if (glance) {
      if (!rows.area) {
        glance.textContent = 'Enter your dimensions to begin';
      } else if (d.materialCost) {
        glance.textContent = fmtNum(d.cubicFeet) + ' cu ft · ' + fmtMoney(d.materialCost);
      } else {
        glance.textContent = fmtArea(d.squareFeet) + ' sq ft · ' + fmtNum(d.cubicFeet) + ' cu ft';
      }
    }
  }

  function wirePanel() {
    var toggleBtn = $('[data-pb-panel-toggle]');
    var body = $('[data-pb-panel-body]');
    if (toggleBtn && body) {
      toggleBtn.addEventListener('click', function () {
        var open = toggleBtn.getAttribute('aria-expanded') === 'true';
        toggleBtn.setAttribute('aria-expanded', String(!open));
        root.classList.toggle('pb--panel-open', !open);
      });
    }

    // The sticky bar mirrors the current step's Continue action so the primary
    // CTA is always reachable on mobile (wireframes README §5).
    var panelNext = $('[data-pb-panel-next]');
    if (panelNext) {
      panelNext.addEventListener('click', function () {
        if (state.step < TOTAL_STEPS) goToStep(state.step + 1);
      });
    }
  }

  /**
   * Send the specialist request and report the TRUTH about what happened.
   *
   * Three outcomes, all shown on the success panel so focus management and the
   * escape route stay identical:
   *   2xx  — genuinely received
   *   501  — implemented-but-blocked; we surface the server's own reason
   *   else — could not reach it; the customer is told plainly and given the phone
   *
   * The form is only hidden AFTER a reply, so a failure never destroys what the
   * customer typed.
   */
  function submitSpecialist(form) {
    var cfg = window.PBConfig;
    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;
    announce('Sending your request.');

    var payload = {
      name: fieldValue(form, 'name'),
      email: fieldValue(form, 'email'),
      phone: fieldValue(form, 'phone'),
      message: fieldValue(form, 'message'),
      // SH-18 — raw inputs only; the server recomputes every derived figure.
      project: {
        shape: state.shape, length: state.length, width: state.width,
        diameter: state.diameter, depth: state.depth, packaging: state.packaging,
        colorMode: state.colorMode, colors: state.colors, zip: state.zip
      }
    };

    fetch(cfg.backend.specialistEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        return { ok: res.ok, status: res.status, data: data };
      });
    }).catch(function () {
      return { ok: false, status: 0, data: {} };
    }).then(function (r) {
      if (submitBtn) submitBtn.disabled = false;
      showSpecialistOutcome(r);
    });
  }

  /** Read a named field's trimmed value, tolerating a missing field. */
  function fieldValue(form, name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el && typeof el.value === 'string' ? el.value.trim() : '';
  }

  /** Render the outcome of a specialist submission. */
  function showSpecialistOutcome(r) {
    var form = $('[data-pb-specialist-form]');
    var heading = $('[data-pb-success-heading]');
    var body = $('[data-pb-success-body]');
    var title;
    var text;

    var data = r.data || {};

    /**
     * The BODY decides, not the status line.
     *
     * Shopify's App Proxy does not pass a non-2xx JSON response through to the
     * storefront — it substitutes its own HTML error page with HTTP 500, so the
     * browser never sees the server's reason. The backend therefore answers 200
     * with `delivered: true|false`, and this reads that.
     */
    if (r.ok && data.delivered === true) {
      /**
       * FRIENDLY, CUSTOMER-FACING COPY (Product Owner, 2026-08-20).
       *
       * This can ONLY render when the server says `delivered: true` — today
       * that never happens, since /specialist always answers `delivered: false`
       * until OQ-23/OQ-26 are resolved (D-045). That is the gate: friendlier
       * wording sits in dead code until the backend genuinely delivers a
       * request, so it cannot be shown to a real customer before the feature
       * is wired up. The honest branch below is what internal testing sees
       * today, and stays exactly that honest.
       */
      title = 'Request sent';
      text = "Thanks — we've received your request. A Rainbow Mulch specialist will follow up shortly.";
    } else if (r.ok && data.delivered === false) {
      /**
       * Received and understood, but not being delivered yet. The reason comes
       * from the SERVER, so this copy cannot contradict the backend and cannot
       * go stale the way the old hardcoded P-001 claim did.
       */
      var blocked = data.blockedBy ? ' (' + data.blockedBy + ')' : '';
      var detail = data.detail ? ' ' + data.detail : '';
      title = 'Not sent yet';
      text = 'Specialist requests are not being delivered yet' + blocked + '.' + detail +
        ' Nothing has been sent, so please contact us directly for now.';
    } else {
      title = 'Could not send';
      text = 'Something went wrong sending your request, and nothing has been sent. ' +
        'Please try again, or contact us directly.';
    }

    /**
     * Deliberately no phone number here. There is no contact number in PBConfig,
     * and inventing one in the theme would be exactly the kind of hardcoded
     * business fact D-010 forbids — and the kind of stale copy this fix exists to
     * remove. If a number should appear, it belongs in config, sourced from the
     * business.
     */

    hide(form);
    show($('[data-pb-overlay-success]'));
    if (heading) { heading.textContent = title; heading.focus(); }
    if (body) body.textContent = text;
    announce(title + '. ' + text, true);
  }

  /* =====================================================================
   * SPECIALIST OVERLAY — D-019
   * Modal requirements are easy to omit and expensive to retrofit, so they are
   * implemented here rather than deferred: focus move, focus trap, Escape,
   * focus return, and a genuinely INERT background.
   * =================================================================== */
  var overlayReturnFocus = null;

  /**
   * D-047 (Sprint 10) — GENERALISED to support a SECOND overlay (the checkout
   * invoice-request form) rather than the single hardcoded `[data-pb-overlay]`
   * this originally assumed. `activeOverlayRoot` tracks whichever overlay is
   * currently open; close/Escape/focus-trap all operate on it, not on a fixed
   * selector, so the SAME machinery serves both without duplicating the a11y
   * work (focus trap, inert background, Escape, focus return) a second time.
   */
  var activeOverlayRoot = null;

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),' +
    'textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

  /**
   * @param {HTMLElement} overlayRoot the specific overlay to open
   * @param {HTMLElement} [trigger] the control that opened it (focus returns here on close)
   * @param {Function} [onOpen] called with overlayRoot before it is shown, for
   *   any overlay-specific reset (e.g. the specialist overlay re-rendering its
   *   attached-project block and resetting to its form view).
   */
  function openOverlay(overlayRoot, trigger, onOpen) {
    if (!overlayRoot) return;

    // Defensive — only one overlay should ever be visible at a time. Added
    // 2026-10-14 for the checkout outcome panel's "Talk to a specialist"
    // button, which opens the specialist overlay FROM INSIDE the checkout
    // overlay; without this, the checkout overlay would stay in the DOM
    // (not hidden) underneath it.
    if (activeOverlayRoot && activeOverlayRoot !== overlayRoot) closeOverlay();

    activeOverlayRoot = overlayRoot;
    overlayReturnFocus = trigger || document.activeElement;
    if (onOpen) onOpen(overlayRoot);

    show(overlayRoot);
    // Scroll lock must go on the document, not on .pb — a class on the builder
    // root cannot stop the page behind it from scrolling.
    document.documentElement.classList.add('pb-overlay-open');
    setInert(true);

    var heading = overlayRoot.querySelector('[data-pb-overlay-heading]');
    if (heading) heading.focus();

    document.addEventListener('keydown', onOverlayKeydown, true);
  }

  function closeOverlay() {
    var overlay = activeOverlayRoot;
    if (!overlay || overlay.hidden) return;

    hide(overlay);
    document.documentElement.classList.remove('pb-overlay-open');
    setInert(false);
    document.removeEventListener('keydown', onOverlayKeydown, true);

    // Focus returns to the TRIGGERING control, not the top of the document. A
    // keyboard user who opened this from Step 2 must land back on Step 2.
    if (overlayReturnFocus && document.contains(overlayReturnFocus)) {
      overlayReturnFocus.focus();
    }
    overlayReturnFocus = null;
    activeOverlayRoot = null;
  }

  /** Background is made genuinely inert, not merely dimmed. */
  function setInert(on) {
    [$('.pb__layout'), $('.pb__progress')].forEach(function (el) {
      if (!el) return;
      if (on) {
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true'); // fallback for older engines
      } else {
        el.removeAttribute('inert');
        el.removeAttribute('aria-hidden');
      }
    });
  }

  function onOverlayKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); closeOverlay(); return; }
    if (e.key !== 'Tab') return;

    var dialog = activeOverlayRoot && activeOverlayRoot.querySelector('[data-pb-overlay-dialog]');
    if (!dialog) return;
    var items = $$(FOCUSABLE, dialog).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    // Trap: Tab cycles within the dialog and cannot reach the page behind.
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  /**
   * The attached project. Partial projects are VALID (D-019): unanswered
   * sections are NAMED rather than omitted, so the customer is not made to feel
   * their submission is inadequate and the specialist knows what is still open.
   */
  function renderAttachedProject() {
    var list = $('[data-pb-attached-list]');
    var title = $('[data-pb-attached-title]');
    var missingEl = $('[data-pb-attached-missing]');
    if (!list) return;

    var d = window.PBCalculator.derive(state);
    var lines = [];
    var missing = [];

    if (stepComplete(1)) {
      lines.push(state.shape === 'circle'
        ? state.diameter + ' ft diameter · ' + state.depth + ' in deep'
        : state.length + ' × ' + state.width + ' ft · ' + state.depth + ' in deep');
      lines.push(fmtNum(d.cubicFeet) + ' cu ft');
    } else {
      missing.push('dimensions');
    }

    if (state.packaging) {
      lines.push(fmtNum(d.unitCount) + ' ' + d.unitNoun + ' (' + d.packagingLabel + ')');
    } else {
      missing.push('packaging');
    }

    if (state.colors.length) {
      var names = state.colors.map(function (id) {
        var c = cfg.color.catalog.filter(function (x) { return x.id === id; })[0];
        return c ? c.name : id;
      });
      lines.push((state.colorMode === 'mix' ? 'Mix Your Own: ' : '') + names.join(' / '));
    } else {
      missing.push('color');
    }

    if (state.zip) {
      lines.push('ZIP ' + state.zip + (state.deliveryType ? ' · ' + capitalise(state.deliveryType) : ''));
    } else {
      missing.push('delivery');
    }

    if (d.total) lines.push('Est. total ' + fmtMoney(d.total));

    var isComplete = missing.length === 0;
    if (title) title.textContent = isComplete ? 'Your project is attached' : 'Your project so far';

    list.innerHTML = '';
    if (!lines.length) {
      var li = document.createElement('li');
      li.textContent = 'You have not configured anything yet — that is fine, we will help from scratch.';
      list.appendChild(li);
    } else {
      lines.forEach(function (text) {
        var li = document.createElement('li');
        li.textContent = text;
        list.appendChild(li);
      });
    }

    if (missingEl) {
      if (missing.length) {
        missingEl.textContent = capitalise(missing.join(', ')) +
          ' not chosen yet — we will help with ' + (missing.length === 1 ? 'that' : 'those') + '.';
        show(missingEl);
      } else {
        hide(missingEl);
      }
    }
  }

  function wireOverlay() {
    var specialistOverlay = $('[data-pb-overlay]');

    $$('[data-pb-overlay-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        openOverlay(specialistOverlay, btn, function () {
          renderAttachedProject();
          // Reset to the form view in case it was left on the success state.
          show($('[data-pb-specialist-form]'));
          hide($('[data-pb-overlay-success]'));
        });
      });
    });

    /**
     * D-047 (Sprint 10) — plural now: TWO overlays share these close controls
     * and backdrops (specialist + checkout invoice-request), each scoped
     * within its own overlay root. closeOverlay() always closes whichever one
     * is actually open (activeOverlayRoot), so wiring every instance to the
     * same handler is correct regardless of which overlay a click came from.
     */
    $$('[data-pb-overlay-close]').forEach(function (btn) {
      btn.addEventListener('click', closeOverlay);
    });
    $$('[data-pb-overlay-backdrop]').forEach(function (backdrop) {
      backdrop.addEventListener('click', closeOverlay);
    });

    var form = $('[data-pb-specialist-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!validateSpecialistForm(form)) return;

        /**
         * ASK THE SERVER WHAT IS TRUE — do not assert it here.
         *
         * This previously hardcoded "sending requires the backend service
         * (P-001), which is still awaiting approval". That went stale the moment
         * P-001 was approved (D-039) and the backend went live (D-043), leaving
         * the overlay telling customers something factually wrong.
         *
         * The fix is not to hardcode a NEWER blocker — that would rot the same
         * way. We POST for real and report what actually happened. The endpoint
         * currently answers 501 with a live `blockedBy`/`detail`, so the message
         * stays accurate on its own; when the route is implemented the same code
         * reports a genuine success with no edit here.
         */
        submitSpecialist(form);
      });
    }
  }

  /** Errors appear per field and are announced; the form is never silently unsubmittable. */
  function validateSpecialistForm(form) {
    var ok = true;
    var checks = [
      { id: 'sp-name', el: form.querySelector('#pb-sp-name'), msg: 'Please tell us your name' },
      { id: 'sp-email', el: form.querySelector('#pb-sp-email'), msg: 'Please enter an email address' }
    ];

    checks.forEach(function (c) {
      var errEl = $('[data-pb-error="' + c.id + '"]');
      var empty = !c.el || !c.el.value.trim();
      var badEmail = c.id === 'sp-email' && c.el && c.el.value && c.el.value.indexOf('@') === -1;
      if (empty || badEmail) {
        ok = false;
        if (errEl) {
          errEl.textContent = badEmail ? 'That email address does not look right' : c.msg;
          show(errEl);
        }
        if (c.el) c.el.setAttribute('aria-invalid', 'true');
      } else {
        if (errEl) hide(errEl);
        if (c.el) c.el.removeAttribute('aria-invalid');
      }
    });

    if (!ok) {
      announce('Please check the highlighted fields.', true);
      var firstBad = form.querySelector('[aria-invalid="true"]');
      if (firstBad) firstBad.focus();
    }
    return ok;
  }

  /* =====================================================================
   * RENDER — one pass, from state. Never reads state back out of the DOM.
   * =================================================================== */
  function render() {
    // THE SEAM (Sprint 4) — real arithmetic now. Freight and tax inside the
    // payload are still mocked; see pb-mocks.js for what remains and why.
    var d = window.PBCalculator.derive(state);

    // Item 1 (2026-09-11) — may force state.packaging to 'bags' if the order
    // just shrank to or below the flat-fee line while Tote was selected.
    // Re-derive afterward so the rest of this pass reflects that, not the
    // packaging value d was computed from a moment ago.
    applyFlatFeeConstraint(d.cubicFeet);
    d = window.PBCalculator.derive(state);

    var flatEligible = window.PBCalculator.isFlatFeeEligible(d.cubicFeet);
    // Item 8 — which Step 4 intro paragraph applies. Driven by ORDER SIZE
    // (item 1's rule), never by packaging alone: a >14 cu ft bag order still
    // ships freight truck, same as a tote order that size always has.
    toggle($('[data-pb-shipping-intro-freight]'), !flatEligible);
    toggle($('[data-pb-shipping-intro-flatfee]'), flatEligible);
    // Item 11 — the static liftgate message only applies to a freight-truck
    // order; a flat-fee UPS parcel never involves one.
    toggle($('[data-pb-liftgate-note]'), !flatEligible);
    var quoteLabel = $('[data-pb-quote-label]');
    if (quoteLabel) quoteLabel.textContent = flatEligible ? 'Get my shipping cost' : 'Get my freight rate';

    setText('out', 'squareFeet', fmtArea(d.squareFeet) + ' sq ft');
    setText('out', 'cubicFeet', fmtNum(d.cubicFeet) + ' cu ft');
    setText('out', 'weightLbs', d.weightLbs ? fmtNum(d.weightLbs) + ' lbs' : '—');
    setText('out', 'zip', state.zip || '—');
    setText('out', 'freightAmount', fmtMoney(d.freightAmount));
    setText('out', 'freightRange', fmtFreightRange(d, false));
    setText('out', 'transitDays', d.transitDaysLabel);
    setText('out', 'deliveryLabel', state.deliveryType ? capitalise(state.deliveryType) + ' delivery' : '—');
    setText('out', 'liftgateLabel', state.liftgate === 'yes' ? 'Liftgate included' : 'No liftgate');

    // Step 2 comparison — both options always shown (BR-09).
    var c = d.comparison;
    setText('cmp', 'bagsCount', fmtNum(c.bags.count) + ' bags');
    setText('cmp', 'bagsCost', fmtMoney(c.bags.cost));
    setText('cmp', 'bagsWeight', fmtNum(c.bags.weightLbs) + ' lbs');
    setText('cmp', 'toteCount', fmtNum(c.tote.count) + ' totes');
    setText('cmp', 'toteCost', fmtMoney(c.tote.cost));
    setText('cmp', 'toteWeight', fmtNum(c.tote.weightLbs) + ' lbs');
    // D-032 — heaviest single tote, shown on the card and in the acknowledgement.
    setText('cmp', 'toteEach', fmtNum(c.tote.heaviestUnitLbs) + ' lbs');
    // Item 7 (2026-09-11) — plural-aware noun for the tote acknowledgement
    // checkbox label ("my Bulk Tote" vs "my Bulk Totes").
    setText('cmp', 'toteAckNoun', c.tote.count === 1 ? 'Tote' : 'Totes');
    // Positive only under D-021 (as-ordered billing).
    setText('cmp', 'toteSaving', c.tote.savingVsBags > 0 ? 'Save ' + fmtMoney(c.tote.savingVsBags) : '');

    // D-028 — the results block appears once the ACTIVE shape's fields are filled.
    toggle($('[data-pb-results]'), stepComplete(1));

    renderPanel(d);
    renderSummary(d);
    renderFreightStates();

    // Gate Continue on the CURRENT step, and mirror it onto the sticky bar.
    var complete = stepComplete(state.step);
    var activePanel = $('[data-pb-panel="' + state.step + '"]');
    if (activePanel) {
      var next = $('[data-pb-next]', activePanel);
      if (next) next.disabled = !complete;
    }
    var panelNext = $('[data-pb-panel-next]');
    if (panelNext) {
      panelNext.disabled = !complete || state.step === TOTAL_STEPS;
      toggle(panelNext, state.step !== TOTAL_STEPS);
    }

    renderProgress();
  }

  /* =====================================================================
   * INIT
   * =================================================================== */
  function wireNavigation() {
    $$('[data-pb-next]').forEach(function (btn) {
      btn.addEventListener('click', function () { goToStep(state.step + 1); });
    });
    $$('[data-pb-back]').forEach(function (btn) {
      btn.addEventListener('click', function () { goToStep(state.step - 1); });
    });
    $$('[data-pb-goto]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        goToStep(Number(btn.getAttribute('data-pb-goto')));
      });
    });
  }

  wireNavigation();
  applyDepthDefault(); // before wiring, so the pre-filled value is not read as customer input
  wireStep1();
  wireStep2();
  wireStep3();
  wireStep4();
  wireStep5();
  wirePanel();
  wireOverlay();
  wireCheckoutOverlay();
  wireProjectEmailOverlay();
  render();

  // Exposed for debugging during review only. Not an API.
  window.PBDebug = { state: state, goToStep: goToStep };
})();
